import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Link, FileText, Loader2, ExternalLink, AlertCircle, CheckCircle, Lightbulb, TrendingUp, TrendingDown, Eye, Hash, Info, XCircle, Lock, Clock } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

// Minimum content length to consider analysis valid
const MIN_ANALYSIS_CONTENT = 100;

// UI States
const UI_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

// Progress stages for status messages
const PROGRESS_STAGES = [
  { threshold: 0, message: 'Starting analysis...' },
  { threshold: 10, message: 'Resolving link...' },
  { threshold: 25, message: 'Fetching article...' },
  { threshold: 50, message: 'Extracting key points...' },
  { threshold: 75, message: 'Generating summary...' },
  { threshold: 90, message: 'Finalizing...' },
  { threshold: 100, message: 'Complete!' }
];

// Progress bar component
const ProgressBar = ({ progress, status }) => {
  const getStageMessage = () => {
    for (let i = PROGRESS_STAGES.length - 1; i >= 0; i--) {
      if (progress >= PROGRESS_STAGES[i].threshold) {
        return PROGRESS_STAGES[i].message;
      }
    }
    return PROGRESS_STAGES[0].message;
  };

  return (
    <div className="w-full space-y-2">
      {/* Progress bar container */}
      <div className="relative w-full h-2 rounded-full overflow-hidden" 
           style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
        {/* Progress fill */}
        <div 
          className="absolute top-0 left-0 h-full rounded-full transition-all duration-150 ease-out"
          style={{ 
            width: `${Math.min(progress, 100)}%`,
            background: progress >= 100 
              ? 'linear-gradient(90deg, #22c55e, #4ade80)' 
              : 'linear-gradient(90deg, #818cf8, #a78bfa)'
          }}
        />
        {/* Shimmer effect */}
        {progress < 100 && (
          <div 
            className="absolute top-0 left-0 h-full animate-pulse"
            style={{ 
              width: `${Math.min(progress, 100)}%`,
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
              animation: 'shimmer 1.5s infinite'
            }}
          />
        )}
      </div>
      
      {/* Status line */}
      <div className="flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5" style={{ color: '#a1a1aa' }}>
          {progress < 100 ? (
            <Loader2 className="h-3 w-3 animate-spin" style={{ color: '#818cf8' }} />
          ) : (
            <CheckCircle className="h-3 w-3" style={{ color: '#22c55e' }} />
          )}
          {status || getStageMessage()}
        </span>
        <span className="font-mono" style={{ color: progress >= 100 ? '#22c55e' : '#818cf8' }}>
          {Math.round(progress)}%
        </span>
      </div>
    </div>
  );
};

const ArticleAnalyzeModal = ({ 
  isOpen, 
  onClose, 
  articleUrl, 
  ticker, 
  companyName, 
  detectedTheme,
  onAnalysisComplete,
  moveContext,  // Pass move info for whyItHasMoved
  fallbackData  // NEW: Fallback headline/summary for when URL fetch fails
}) => {
  const { token, isPremium } = useAuth();
  const [activeTab, setActiveTab] = useState('url');
  const [url, setUrl] = useState(articleUrl || '');
  const [pastedText, setPastedText] = useState('');
  const [uiState, setUiState] = useState(UI_STATES.IDLE);
  const [error, setError] = useState(null);
  const [domainAllowed, setDomainAllowed] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const urlInputRef = useRef(null);
  
  // Usage tracking state
  const [usageInfo, setUsageInfo] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [loadingUsage, setLoadingUsage] = useState(false);
  
  // Progress bar state
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState('');
  const progressIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // Fetch usage info when modal opens
  useEffect(() => {
    if (isOpen && token) {
      fetchUsageInfo();
    }
  }, [isOpen, token]);
  
  const fetchUsageInfo = async () => {
    if (!token) return;
    setLoadingUsage(true);
    try {
      const response = await axios.get(`${API_BASE}/usage/ai-article-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageInfo(response.data);
    } catch (err) {
      console.error('Error fetching usage info:', err);
    } finally {
      setLoadingUsage(false);
    }
  };
  
  // Format reset time as HH:MM
  const formatResetTime = (seconds) => {
    if (!seconds) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Smart simulated progress algorithm
  const startProgressSimulation = useCallback(() => {
    setProgress(0);
    setProgressStatus('Starting analysis...');
    
    let currentProgress = 0;
    const startTime = Date.now();
    
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      
      // Smart progress algorithm:
      // 0-35% fast (first 1 second) - immediate feedback
      // 35-85% slow (next 19 seconds) - steady progress
      // Cap at 90% until response arrives
      
      if (currentProgress < 35) {
        // Fast phase: 0 → 35% in ~1 second
        currentProgress += 3.5;
      } else if (currentProgress < 85) {
        // Slow phase: 35 → 85% over ~19 seconds
        // Smaller increments as we approach 85%
        const remaining = 85 - currentProgress;
        const increment = Math.max(0.3, remaining * 0.05);
        currentProgress += increment;
      } else if (currentProgress < 90) {
        // Very slow: 85 → 90%
        currentProgress += 0.1;
      }
      // Cap at 90% - never exceed until response
      currentProgress = Math.min(currentProgress, 90);
      
      setProgress(currentProgress);
      
      // Update status based on progress
      if (currentProgress < 15) {
        setProgressStatus('Resolving link...');
      } else if (currentProgress < 35) {
        setProgressStatus('Fetching article...');
      } else if (currentProgress < 60) {
        setProgressStatus('Extracting key points...');
      } else if (currentProgress < 80) {
        setProgressStatus('Generating summary...');
      } else if (elapsed > 15000) {
        setProgressStatus('Still working... please wait');
      } else {
        setProgressStatus('Finalizing...');
      }
    }, 120);
  }, []);

  const stopProgressSimulation = useCallback((success = true) => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    if (success) {
      // Animate to 100%
      setProgress(100);
      setProgressStatus('Complete!');
    } else {
      // Reset on error
      setProgress(0);
      setProgressStatus('');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUrl(articleUrl || '');
      setError(null);
      setDomainAllowed(null);
      setUiState(UI_STATES.IDLE);
      setAnalysisResult(null);
      setProgress(0);
      setProgressStatus('');
      
      // Focus input
      setTimeout(() => {
        if (urlInputRef.current) {
          urlInputRef.current.focus();
        }
      }, 100);
      
      // Check domain if URL prefilled
      if (articleUrl) {
        checkDomain(articleUrl);
      }
      
      // Log for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('[ArticleAnalyzer] Modal opened with URL:', articleUrl);
      }
    } else {
      // Cleanup when modal closes
      stopProgressSimulation(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [isOpen, articleUrl, stopProgressSimulation]);

  // Check if domain is allowed when URL changes
  const checkDomain = async (urlToCheck) => {
    if (!urlToCheck || urlToCheck.length < 10) {
      setDomainAllowed(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API_BASE}/article/check-domain`, {
        params: { url: urlToCheck }
      });
      setDomainAllowed(response.data);
    } catch (err) {
      setDomainAllowed({ allowed: false, message: 'Could not verify domain' });
    }
  };

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    setError(null);
    
    // Debounce domain check
    clearTimeout(window.domainCheckTimeout);
    window.domainCheckTimeout = setTimeout(() => {
      checkDomain(newUrl);
    }, 500);
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    stopProgressSimulation(false);
    setUiState(UI_STATES.IDLE);
    setError(null);
  };

  const handleAnalyze = async (mode) => {
    setUiState(UI_STATES.LOADING);
    setError(null);
    setAnalysisResult(null);
    
    // Start progress simulation
    startProgressSimulation();
    
    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();
    
    const inputUrl = mode === 'url' ? url : null;
    
    // Log for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('[ArticleAnalyzer] Starting analysis:', {
        inputUrl,
        mode,
        ticker,
        detectedTheme
      });
    }
    
    try {
      const payload = {
        ticker,
        companyName,
        url: inputUrl,
        articleText: mode === 'paste' ? pastedText : null,
        detectedTheme,
        moveContext,  // Pass move context for whyItHasMoved
        // Fallback data for when URL fetch fails (from news provider)
        fallbackHeadline: fallbackData?.headline || null,
        fallbackSummary: fallbackData?.summary || null,
        fallbackSource: fallbackData?.source || null
      };
      
      // Include auth token if user is logged in
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API_BASE}/article/analyze`, payload, { 
        headers,
        signal: abortControllerRef.current.signal
      });
      const data = response.data;
      
      // Log response for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log('[ArticleAnalyzer] Response:', {
          status: data.status,
          detected_input: data.detected_input,
          article_text_length: data.article_text_length,
          hasAnalysis: !!data.analysis,
          error: data.error
        });
      }
      
      // STRICT VALIDATION: Only succeed if we have real content
      const isRealSuccess = 
        data.status === 'success' &&
        data.article_text_length >= MIN_ANALYSIS_CONTENT &&
        data.analysis &&
        (data.analysis.inSimpleTerms || data.analysis.keyPoints?.length > 0);
      
      if (isRealSuccess) {
        // Stop simulation and animate to 100%
        stopProgressSimulation(true);
        
        // Small delay to show 100% before revealing content
        setTimeout(() => {
          setUiState(UI_STATES.SUCCESS);
          setAnalysisResult(data);
          onAnalysisComplete(data);
        }, 300);
      } else if (data.status === 'error' || data.error) {
        // Explicit error from backend
        stopProgressSimulation(false);
        const errorInfo = data.error || {};
        setError({
          message: errorInfo.message_user || data.error || 'Analysis failed',
          hints: errorInfo.hints || [],
          code: errorInfo.code || 'UNKNOWN_ERROR'
        });
        setUiState(UI_STATES.ERROR);
      } else {
        // Response succeeded but no real content - treat as error
        stopProgressSimulation(false);
        setError({
          message: "Analysis completed but no meaningful content was extracted. Please paste the article text instead.",
          hints: [
            'The article page may be behind a paywall',
            'Try copying and pasting the article text directly',
            'Make sure the URL points to an article page, not an API endpoint'
          ],
          code: 'EMPTY_ANALYSIS'
        });
        setUiState(UI_STATES.ERROR);
      }
    } catch (err) {
      // Stop progress simulation on error
      stopProgressSimulation(false);
      
      // Check if request was cancelled
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        setUiState(UI_STATES.IDLE);
        return;
      }
      
      const errorDetail = err.response?.data?.detail || err.response?.data || {};
      const errorInfo = errorDetail.error || {};
      const statusCode = err.response?.status;
      
      // Extract stage and reason from structured error
      const errorStage = errorInfo.stage || 'unknown';
      const errorReason = errorInfo.reason || 'unknown';
      let errorCode = errorInfo.code || errorDetail.error_code || 'REQUEST_ERROR';
      
      // Build user-friendly error message based on stage
      let userMessage = errorInfo.message_user || errorDetail.error || err.message || 'Failed to analyze article';
      let errorHints = errorInfo.hints || [];
      
      // Stage-specific error messages
      if (errorStage === 'resolve') {
        userMessage = userMessage || "Could not resolve the article URL. Please paste the article text instead.";
        if (!errorHints.length) {
          errorHints = [
            'The news link could not be resolved to an article',
            'Try opening the article in your browser and copying the URL',
            'Or use the "Paste Text" tab'
          ];
        }
      } else if (errorStage === 'fetch') {
        if (errorReason === 'blocked' || errorReason === 'auth_required') {
          userMessage = "This publisher blocks automated access. Please open the article and paste the text.";
        } else if (errorReason === 'timeout') {
          userMessage = "The page took too long to load. Please paste the article text instead.";
        } else if (errorReason === 'rate_limited') {
          userMessage = "Too many requests. Please wait a moment and try again.";
        }
        if (!errorHints.length) {
          errorHints = ['Open the article in your browser', 'Copy the text and use "Paste Text" mode'];
        }
      } else if (errorStage === 'extract') {
        userMessage = userMessage || "Could not extract article content. The page may require JavaScript or a subscription.";
        if (!errorHints.length) {
          errorHints = ['The page content could not be extracted', 'Please paste the article text manually'];
        }
      } else if (errorStage === 'summarize') {
        if (errorReason === 'rate_limited') {
          userMessage = "AI service is temporarily busy. Please try again in a moment.";
          errorHints = ['Wait a few seconds and try again'];
        } else {
          userMessage = userMessage || "Analysis could not be completed. Please try again.";
        }
      }
      
      // Handle specific HTTP status codes (fallback)
      if (statusCode === 520 || statusCode === 502) {
        if (!errorStage || errorStage === 'unknown') {
          userMessage = "Unable to fetch the article. The page may be blocked or temporarily unavailable.";
          errorHints = [
            'Try opening the article in your browser first',
            'Copy the article text and use "Paste Text" mode'
          ];
          errorCode = 'FETCH_ERROR';
        }
      } else if (statusCode === 429) {
        // Check if it's a rate limit error for article analysis
        const limitReached = errorDetail.code === 'LIMIT_REACHED' || errorInfo.code === 'LIMIT_REACHED';
        if (limitReached) {
          // Show upgrade prompt for limit reached
          setShowUpgradePrompt(true);
          setUsageInfo({
            limit: errorDetail.limit || 2,
            used: errorDetail.used || 2,
            remaining: 0,
            reset_in_seconds: errorDetail.reset_in_seconds,
            is_pro: false
          });
          userMessage = errorDetail.message || errorDetail.detail || "Free plan includes 2 AI article analyses per 24 hours.";
          errorHints = ['Upgrade to Pro for unlimited access', 'Wait for the limit to reset'];
          errorCode = 'LIMIT_REACHED';
        } else {
          userMessage = "Rate limited. Please wait a moment and try again.";
          errorHints = ['Wait a few seconds and try again', 'Use the "Paste Text" option instead'];
          errorCode = 'RATE_LIMITED';
        }
      } else if (statusCode === 422) {
        userMessage = userMessage || "Could not extract article content. The page may require a subscription or be blocked.";
        errorHints = ['Copy the article text manually', 'Use the "Paste Text" tab'];
        errorCode = 'EXTRACTION_FAILED';
      }
      
      setError({
        message: userMessage,
        hints: errorHints,
        code: errorCode,
        stage: errorStage,
        reason: errorReason
      });
      setUiState(UI_STATES.ERROR);
      
      // Auto-switch to paste tab for certain errors
      const switchToPasteErrors = [
        'DOMAIN_NOT_ALLOWED', 
        'FINNHUB_API_URL', 
        'EXTRACTION_FAILED',
        'FETCH_ERROR',
        'CONTENT_BLOCKED',
        'AUTH_REQUIRED',
        'PAYMENT_REQUIRED',
        'FORBIDDEN',
        'CONTENT_TOO_SHORT'
      ];
      const switchToPasteStages = ['resolve', 'fetch', 'extract'];
      
      if (switchToPasteErrors.includes(errorCode) || switchToPasteStages.includes(errorStage)) {
        setActiveTab('paste');
      }
      
      // Log for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('[ArticleAnalyzer] Error:', { statusCode, errorCode, errorStage, errorReason, userMessage, err });
      }
    }
  };

  const isLoading = uiState === UI_STATES.LOADING;

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div 
        className="w-full max-w-2xl rounded-xl overflow-hidden"
        style={{ 
          backgroundColor: '#09090b',
          border: '1px solid rgba(255,255,255,0.1)'
        }}
        data-testid="article-analyze-modal"
        data-tour="aa-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" 
             style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <div>
            <h2 className="text-base font-medium" style={{ color: '#fafafa' }}>
              Analyze Article
            </h2>
            <p className="text-xs mt-0.5" style={{ color: '#71717a' }}>
              {ticker} • {companyName}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-white/5 transition-colors"
            data-tour="aa-close"
          >
            <X className="h-4 w-4" style={{ color: '#71717a' }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          <button
            onClick={() => setActiveTab('url')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'url' 
                ? 'border-b-2' 
                : ''
            }`}
            style={{ 
              color: activeTab === 'url' ? '#a5b4fc' : '#71717a',
              borderColor: activeTab === 'url' ? '#6366f1' : 'transparent',
              backgroundColor: activeTab === 'url' ? 'rgba(99, 102, 241, 0.05)' : 'transparent'
            }}
          >
            <Link className="h-4 w-4" />
            From URL
          </button>
          <button
            onClick={() => setActiveTab('paste')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === 'paste' 
                ? 'border-b-2' 
                : ''
            }`}
            style={{ 
              color: activeTab === 'paste' ? '#a5b4fc' : '#71717a',
              borderColor: activeTab === 'paste' ? '#6366f1' : 'transparent',
              backgroundColor: activeTab === 'paste' ? 'rgba(99, 102, 241, 0.05)' : 'transparent'
            }}
          >
            <FileText className="h-4 w-4" />
            Paste Text
          </button>
        </div>

        {/* Usage Info Display (for free users) */}
        {usageInfo && !usageInfo.is_pro && uiState !== UI_STATES.SUCCESS && (
          <div 
            className="px-5 py-2 flex items-center justify-between text-xs"
            style={{ 
              backgroundColor: 'rgba(139, 92, 246, 0.05)',
              borderBottom: '1px solid rgba(139, 92, 246, 0.1)'
            }}
          >
            <div className="flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" style={{ color: '#A78BFA' }} />
              <span style={{ color: '#A78BFA' }}>
                Free: {usageInfo.used}/{usageInfo.limit} analyses used
              </span>
              {usageInfo.reset_in_seconds && usageInfo.used >= usageInfo.limit && (
                <span style={{ color: '#71717a' }}>
                  (resets in {formatResetTime(usageInfo.reset_in_seconds)})
                </span>
              )}
            </div>
            {usageInfo.remaining === 0 && (
              <button
                onClick={() => setShowUpgradePrompt(true)}
                className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors"
                style={{ 
                  backgroundColor: 'rgba(139, 92, 246, 0.15)',
                  color: '#A78BFA',
                  border: '1px solid rgba(139, 92, 246, 0.3)'
                }}
              >
                <Lock className="h-3 w-3" />
                Upgrade
              </button>
            )}
          </div>
        )}
        
        {/* Pro Badge (for pro users) */}
        {usageInfo?.is_pro && uiState !== UI_STATES.SUCCESS && (
          <div 
            className="px-5 py-2 flex items-center gap-2 text-xs"
            style={{ 
              backgroundColor: 'rgba(34, 197, 94, 0.05)',
              borderBottom: '1px solid rgba(34, 197, 94, 0.1)'
            }}
          >
            <CheckCircle className="h-3.5 w-3.5" style={{ color: '#22c55e' }} />
            <span style={{ color: '#22c55e' }}>Pro: Unlimited analyses</span>
          </div>
        )}

        {/* Upgrade Prompt Modal */}
        {showUpgradePrompt && (
          <div 
            className="absolute inset-0 z-50 flex items-center justify-center p-6"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
          >
            <div 
              className="w-full max-w-sm p-6 rounded-xl text-center"
              style={{ 
                backgroundColor: '#1a1a2e',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              <div 
                className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}
              >
                <Lock className="h-6 w-6" style={{ color: '#A78BFA' }} />
              </div>
              
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#F8FAFC' }}>
                Unlock Unlimited AI Article Analysis
              </h3>
              
              <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                Free includes 2 analyses per 24 hours. Upgrade to Pro for unlimited article breakdowns.
              </p>
              
              {usageInfo?.reset_in_seconds && (
                <p className="text-xs mb-4" style={{ color: '#71717a' }}>
                  Your limit resets in {formatResetTime(usageInfo.reset_in_seconds)}
                </p>
              )}
              
              <div className="flex gap-3 justify-center">
                <Button
                  onClick={() => setShowUpgradePrompt(false)}
                  variant="outline"
                  size="sm"
                  className="px-4"
                  style={{ 
                    borderColor: 'rgba(255,255,255,0.1)',
                    color: '#94A3B8'
                  }}
                >
                  Not now
                </Button>
                <Button
                  onClick={() => {
                    window.location.href = '/billing';
                  }}
                  size="sm"
                  className="px-4"
                  style={{ 
                    backgroundColor: '#8B5CF6',
                    color: 'white'
                  }}
                >
                  Upgrade to Pro
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {/* SUCCESS STATE - Show Analysis Result */}
          {uiState === UI_STATES.SUCCESS && analysisResult && (
            <div className="space-y-4" data-tour="aa-results">
              {/* Success Header */}
              <div 
                className="flex items-center gap-2 pb-3 border-b" 
                style={{ borderColor: 'rgba(255,255,255,0.1)' }}
              >
                <CheckCircle className="h-5 w-5" style={{ color: '#22c55e' }} />
                <span className="text-sm font-medium" style={{ color: '#22c55e' }}>Article Analyzed</span>
                {analysisResult.resolved_article_url && (
                  <a 
                    href={analysisResult.resolved_article_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ml-auto flex items-center gap-1 text-xs hover:underline"
                    style={{ color: '#a5b4fc' }}
                  >
                    <ExternalLink className="h-3 w-3" />
                    View Source
                  </a>
                )}
              </div>

              {/* Summary-based analysis notice */}
              {(analysisResult.is_summary_based || analysisResult.analysis?.attribution?.is_summary_based) && (
                <div className="p-3 rounded-lg flex items-start gap-2" style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#fbbf24' }} />
                  <div>
                    <p className="text-sm" style={{ color: '#fcd34d' }}>
                      {analysisResult.summary_notice || analysisResult.analysis?.summary_notice || 'Analysis based on news summary (full article blocked by publisher).'}
                    </p>
                    {(analysisResult.analysis?.attribution?.url || analysisResult.resolved_article_url) && (
                      <a
                        href={analysisResult.analysis?.attribution?.url || analysisResult.resolved_article_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-1 text-xs hover:underline"
                        style={{ color: '#fbbf24' }}
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open full article in browser
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* In Simple Terms */}
              {(analysisResult.inSimpleTerms || analysisResult.analysis?.inSimpleTerms) && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)' }}>
                  <p className="text-sm" style={{ color: '#e4e4e7' }}>
                    {analysisResult.inSimpleTerms || analysisResult.analysis?.inSimpleTerms}
                  </p>
                </div>
              )}

              {/* Why It Has Moved - 3 sentences */}
              {(analysisResult.whyItHasMoved || analysisResult.analysis?.whyItHasMoved)?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: '#818cf8' }}>
                    <TrendingUp className="h-3.5 w-3.5" />
                    Why It May Have Moved
                  </h4>
                  <ul className="space-y-1.5">
                    {(analysisResult.whyItHasMoved || analysisResult.analysis?.whyItHasMoved).map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#a1a1aa' }}>
                        <span style={{ color: '#818cf8' }}>•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Key Points */}
              {(analysisResult.keyPoints || analysisResult.analysis?.keyPoints)?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: '#4ade80' }}>
                    <Hash className="h-3.5 w-3.5" />
                    Key Points
                  </h4>
                  <ul className="space-y-1.5">
                    {(analysisResult.keyPoints || analysisResult.analysis?.keyPoints).slice(0, 4).map((point, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#a1a1aa' }}>
                        <span style={{ color: '#4ade80' }}>•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risks */}
              {(analysisResult.risks || analysisResult.analysis?.risks)?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: '#f87171' }}>
                    <AlertCircle className="h-3.5 w-3.5" />
                    Risks to Consider
                  </h4>
                  <ul className="space-y-1.5">
                    {(analysisResult.risks || analysisResult.analysis?.risks).slice(0, 3).map((risk, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#a1a1aa' }}>
                        <span style={{ color: '#f87171' }}>•</span>
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What to Watch */}
              {(analysisResult.whatToWatchNext || analysisResult.analysis?.whatToWatchNext)?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: '#fbbf24' }}>
                    <Eye className="h-3.5 w-3.5" />
                    What to Watch Next
                  </h4>
                  <ul className="space-y-1.5">
                    {(analysisResult.whatToWatchNext || analysisResult.analysis?.whatToWatchNext).slice(0, 3).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: '#a1a1aa' }}>
                        <span style={{ color: '#fbbf24' }}>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Footer */}
              <div className="pt-3 border-t flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <p className="text-[10px]" style={{ color: '#52525b' }}>
                  For research only. Not financial advice.
                </p>
                <Button
                  onClick={() => {
                    setUiState(UI_STATES.IDLE);
                    setAnalysisResult(null);
                    setError(null);
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                >
                  Analyze Another
                </Button>
              </div>
            </div>
          )}

          {/* ERROR and INPUT STATES */}
          {uiState !== UI_STATES.SUCCESS && (
            <>
              {/* Error Display - Enhanced with hints and action button */}
              {error && (
                <div 
                  className="mb-4 p-3 rounded-lg"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
                >
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                    <div className="flex-1">
                      <p className="text-sm" style={{ color: '#fca5a5' }}>{error.message}</p>
                      
                      {/* Stage indicator for debugging */}
                      {error.stage && error.stage !== 'unknown' && (
                        <p className="text-[10px] mt-1" style={{ color: '#71717a' }}>
                          Stage: {error.stage}{error.reason ? ` (${error.reason})` : ''}
                        </p>
                      )}
                      
                      {/* Hints */}
                      {error.hints && error.hints.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {error.hints.map((hint, idx) => (
                            <li key={idx} className="flex items-start gap-1.5 text-xs" style={{ color: '#a1a1aa' }}>
                              <Lightbulb className="h-3 w-3 flex-shrink-0 mt-0.5" style={{ color: '#fbbf24' }} />
                              {hint}
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      {/* Switch to Paste Text button for non-summarize errors */}
                      {activeTab === 'url' && error.stage !== 'summarize' && (
                        <button
                          onClick={() => {
                            setActiveTab('paste');
                            setError(null);
                          }}
                          className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors hover:bg-white/10"
                          style={{ 
                            backgroundColor: 'rgba(99, 102, 241, 0.15)',
                            color: '#a5b4fc',
                            border: '1px solid rgba(99, 102, 241, 0.3)'
                          }}
                          data-testid="switch-to-paste-button"
                        >
                          <FileText className="h-3 w-3" />
                          Switch to Paste Text
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'url' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: '#71717a' }}>
                      Article URL
                    </label>
                <input
                  ref={urlInputRef}
                  type="url"
                  value={url}
                  onChange={handleUrlChange}
                  placeholder="Insert article URL"
                  className="w-full px-3 py-2.5 rounded-lg text-sm"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fafafa'
                  }}
                  data-testid="article-url-input"
                />
                
                {/* Domain status */}
                {domainAllowed && (
                  <div className="flex items-start gap-1.5 mt-2">
                    {domainAllowed.isFinnhubApi ? (
                      // Special case: Finnhub API URL - now resolvable
                      domainAllowed.canResolve ? (
                        <>
                          <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: '#22c55e' }} />
                          <div>
                            <span className="text-xs" style={{ color: '#22c55e' }}>
                              Finnhub link detected - will auto-resolve to original article
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: '#f87171' }} />
                          <div>
                            <span className="text-xs" style={{ color: '#f87171' }}>
                              This is a Finnhub API link, not an article URL
                            </span>
                            <p className="text-xs mt-1" style={{ color: '#71717a' }}>
                              Click the article title to open the original source, then copy that URL. Or use "Paste Text" mode.
                            </p>
                          </div>
                        </>
                      )
                    ) : domainAllowed.allowed ? (
                      <>
                        <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#22c55e' }} />
                        <span className="text-xs" style={{ color: '#22c55e' }}>
                          {domainAllowed.domain} is enabled for analysis
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#f59e0b' }} />
                        <span className="text-xs" style={{ color: '#f59e0b' }}>
                          {domainAllowed.message}
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <p className="text-xs" style={{ color: '#52525b' }}>
                Supported sources include Reuters, Bloomberg, CNBC, Yahoo Finance, Seeking Alpha, and major wire services.
              </p>

              {/* Progress bar during loading */}
              {isLoading && (
                <div className="py-3">
                  <ProgressBar progress={progress} status={progressStatus} />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => handleAnalyze('url')}
                  disabled={isLoading || !url || (domainAllowed && !domainAllowed.allowed)}
                  className="flex-1"
                  data-testid="fetch-analyze-button"
                  data-tour="aa-fetch-analyze"
                >
                  {isLoading ? 'Analyzing...' : 'Fetch & Analyze'}
                </Button>
                {isLoading && (
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="px-3"
                    style={{ 
                      borderColor: 'rgba(239, 68, 68, 0.3)',
                      color: '#ef4444'
                    }}
                    data-testid="cancel-button"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: '#71717a' }}>
                  Article Text
                </label>
                <textarea
                  value={pastedText}
                  onChange={(e) => {
                    setPastedText(e.target.value);
                    setError(null);
                  }}
                  placeholder="Paste the full article text here (minimum 100 characters)..."
                  rows={10}
                  className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fafafa'
                  }}
                  disabled={isLoading}
                  data-testid="article-text-input"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs" style={{ color: '#52525b' }}>
                    Min 100 characters
                  </span>
                  <span className="text-xs" style={{ color: pastedText.length >= 100 ? '#22c55e' : '#71717a' }}>
                    {pastedText.length.toLocaleString()} / 20,000
                  </span>
                </div>
              </div>

              {/* Progress bar during loading */}
              {isLoading && (
                <div className="py-3">
                  <ProgressBar progress={progress} status={progressStatus} />
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={() => handleAnalyze('paste')}
                  disabled={isLoading || pastedText.length < 100}
                  className="flex-1"
                  data-testid="analyze-pasted-button"
                >
                  {isLoading ? 'Analyzing...' : 'Analyze Pasted Text'}
                </Button>
                {isLoading && (
                  <Button
                    onClick={handleCancel}
                    variant="outline"
                    className="px-3"
                    style={{ 
                      borderColor: 'rgba(239, 68, 68, 0.3)',
                      color: '#ef4444'
                    }}
                    data-testid="cancel-button-paste"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)', backgroundColor: 'rgba(0,0,0,0.2)' }}>
          <p className="text-[10px]" style={{ color: '#52525b' }}>
            Analysis extracts facts from the provided text. No paywalls are bypassed. Content is processed server-side.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ArticleAnalyzeModal;
